import type {FunctionComponent, SyntheticEvent} from 'react'
import React from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'
import clsx from 'clsx'
import {IconAmazonLogo} from '~/icons/icon-amazon-logo'
import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {objectToQueryString} from '~/utils/commons'
import {IconAmazonLogoSvg} from '~/icons/product-page/icon-amazon-logo-svg'
import type {Product, RootAndListContext, RootLoaderResponse} from '~/types'
import {longWordsLanguages, getTranslation} from '~/localization/translation'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import {DiscountBadge} from '../badges/discount-badge'
import ProductCardBadge from '../badges/card-placement-badge'
import VariantsIndication from '../badges/variants-indication'
import {DiscountBadgeReorganized} from '../badges/discount-badge-reorganized'
import SaleEventBadge from '../badges/saleEventBadge'

export interface ProductSponsoredCardProps {
  index: number
  showListingsCta: boolean
  product: Product
}

export const ProductSponsoredCard: FunctionComponent<ProductSponsoredCardProps> = ({
  product,
  showListingsCta,
  index = 0,
}) => {
  const {
    image,
    name,
    brand = '',
    affiliateLink,
    discount,
    badge,
    coupon,
    asin,
    isSellerProduct,
    price,
    forcedPosition,
  } = product
  const {locale, featureFlags, language, trackAnalytics, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const {
    dataResponse: {productsCategory},
    clickId,
    setShowCookieConsentGdpr,
  } = useOutletContext<RootAndListContext>()
  const translations = getTranslation(language)
  const submit = useSubmit()
  const isDiscountExist = Boolean(discount)
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  const shouldTrackAnalytics = Boolean(
    featureFlags?.cookieConsentVariation === 'opt-in' &&
    (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
  )

  const showVariantsIndication =
    featureFlags?.showVariantsIndication && product?.variationDetails && product?.variationDetails.length > 0

  const trackSearchParams = objectToQueryString({
    session_id: trackAnalytics?.session_id,
    ...trackAnalytics?.pageLoadData,
    page_id: trackAnalytics?.page_id,
    user_id: trackAnalytics?.user_id,
  })

  const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  const reportWidgetClick = useReportWidgetClick()
  const reportClickout = useSendPartnerProductClickout({
    product,
    index,
    locale,
    listName: 'sponsored-products',
    categorySlug: productsCategory?.slug,
    categoryName: productsCategory?.name,
  })

  const [cardRef] = useReportItemSeen(
    {
      item_list_id: productsCategory?.slug,
      item_name: name,
      item_source: 'widget',
      partner_item_brand: brand,
      partner_item_id: asin,
      affiliation: affiliateLink.type,
      is_dws: isSellerProduct,
      price: price?.amount,
      currency: price?.currency,
      click_element_name: 'sponsored-product',
      index,
      variation: JSON.stringify({
        hasVariantIndication: showVariantsIndication,
        forcedPosition,
      }),
    },
    {},
  )

  const handleCardClick = (_e: SyntheticEvent) => {
    if (shouldTrackAnalytics) {
      setShowCookieConsentGdpr(false)
      grantAllConsent()
      submit(
        {
          'accept-gdpr': 'granted',
          redirect: 'true',
          'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
        },
        {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
      )
    }

    reportClickout()
    reportWidgetClick({
      widget_name: 'sponsored-product-card',
      widget_place: 'top',
      variation: JSON.stringify({
        forcedPosition,
      }),
    })

    if (typeof Storage != 'undefined') {
      localStorage.setItem('clickTime', Date.now().toString())
      if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
    }
  }

  const reportVariantsClicked = () => {
    reportWidgetClick({
      widget_name: 'variants-indication',
      widget_place: `sponsored-card-${index + 1}`,
      trigger_type: 'click',
      variation: 'sponsored-card',
    })
  }

  return (
    <>
      <div
        data-testid="sponsored-product-card"
        className={clsx(
          'border-gray relative z-20 flex h-full min-h-52 w-full flex-col rounded-3xl border bg-white shadow',
          stylesBydomain.LISTPAGE_SPONSORED_CARD_BORDER,
          'listpage_sponsored_card_border',
        )}
      >
        <ProductCardBadge
          index={index}
          badge={badge}
          coupon={coupon}
          shouldRemoveLogoFromBadge={featureFlags?.useReorganizedCardLayout}
          className="absolute left-6 top-0 overflow-hidden rounded-b-lg rounded-t-none"
        />

        {isDiscountExist && featureFlags?.useReorganizedCardLayout ? (
          <DiscountBadgeReorganized
            className={clsx(
              'absolute right-0 top-[18px] hidden rounded-l-2xl px-3 py-0.5 tablet:block',
              stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_BG,
              stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_TEXT,
              'listpage_badge_card_discount_bg listpage_badge_card_discount_text',
            )}
            discount={discount}
          />
        ) : null}

        <div className="flex min-h-52 w-full tablet:p-5">
          <div className="flex w-full flex-row tablet:flex-col tablet:gap-6">
            <div
              className={clsx(
                'flex tablet:mt-8',
                featureFlags?.useReorganizedCardLayout ? 'desktop:flex-row tablet:flex-row-reverse' : 'flex-row',
              )}
            >
              <div
                className="relative flex min-w-44 flex-col items-center justify-center tablet:min-w-36"
                ref={cardRef}
              >
                {/* ------------------------------------------------ Image ------------------------------------------------ */}
                <div className="flex flex-col items-center justify-center">
                  <a
                    href={redirectUrl}
                    onClick={handleCardClick}
                    aria-label={`view ${brand}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    data-testid="sponsored-product-card-anchor"
                  >
                    <img
                      src={image}
                      loading={index > 2 ? 'lazy' : 'eager'}
                      alt={name?.slice(0, 10)}
                      className="h-[120px] w-[120px] max-w-[120px] shrink-0 grow-0 object-scale-down mix-blend-multiply"
                    />
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-1 desktop:py-7 desktop:pr-4 tablet:flex-col tablet:justify-center tablet:gap-0">
                <div className="flex w-full items-center desktop:flex-wrap tablet:justify-between tablet:gap-y-2">
                  <div className="flex flex-row items-center">
                    {/* ------------------------------------------------------ Brand ------------------------------------------------ */}
                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    >
                      <p
                        className="mr-2 line-clamp-1 text-base font-bold capitalize"
                        data-testid="sponsored-product-card-brand"
                      >
                        {brand}
                      </p>
                    </a>
                    {/* ------------------------------------------------------ Discount ------------------------------------------------ */}
                    {!featureFlags?.useReorganizedCardLayout && isDiscountExist && (
                      <DiscountBadge
                        discount={discount}
                        className={clsx(
                          'mr-2 h-auto max-w-fit rounded-2xl px-2 py-1 text-sm tablet:hidden',
                          stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_OLD_BG,
                          stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_OLD_TEXT,
                          'listpage_badge_card_discount_old_bg listpage_badge_card_discount_old_text',
                        )}
                      />
                    )}
                  </div>
                </div>

                {/* ------------------------------------------------------ Name ------------------------------------------------ */}
                <a
                  href={redirectUrl}
                  onClick={handleCardClick}
                  aria-label={`view ${brand}`}
                  target="_blank"
                  rel="sponsored noreferrer"
                >
                  <h2 className="line-clamp-3 overflow-hidden text-lg font-normal leading-7 no-underline tablet:my-1 tablet:line-clamp-2 tablet:leading-7">
                    {name}
                  </h2>
                </a>

                {/* ------------------------------------------------ Variants Desktop------------------------------------------------ */}
                <div className="absolute bottom-3 flex flex-row items-center gap-1 tablet:hidden">
                  {showVariantsIndication && (
                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    >
                      <VariantsIndication
                        className="flex flex-row gap-1 truncate"
                        index={index + 1}
                        variations={product?.variationDetails}
                        reportVariantsClicked={reportVariantsClicked}
                      />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div
              className={clsx(
                'ml-auto flex flex-col items-center justify-center gap-4 border-0 border-l border-solid tablet:ml-0 tablet:min-w-full tablet:border-none',
                showListingsCta ? 'w-64 min-w-64 desktop:px-2' : 'w-56 min-w-56 desktop:px-4',
              )}
            >
              <div
                className={clsx(
                  'flex w-full items-center justify-center gap-4',
                  featureFlags?.useReorganizedCardLayout ? 'desktop:flex-col tablet:flex-col-reverse' : 'flex-col',
                )}
              >
                <div className="flex flex-col tablet:hidden">
                  <div className="flex justify-center">
                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    >
                      <IconAmazonLogo />
                    </a>
                  </div>
                </div>
                {/* ------------------------------------------------ CTA ------------------------------------------------ */}
                <div className="z-20 flex w-full flex-col">
                  <div className="flex w-full flex-col items-center gap-4 tablet:flex-row">
                    {featureFlags?.useReorganizedCardLayout ? (
                      <a
                        className="mt-1 hidden h-6 w-[80px] object-scale-down tablet:block"
                        href={redirectUrl}
                        onClick={handleCardClick}
                        aria-label={`view ${brand}`}
                        target="_blank"
                        rel="sponsored noreferrer"
                      >
                        <IconAmazonLogoSvg className="h-6 w-[80px] object-scale-down" />
                      </a>
                    ) : null}
                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      target="_blank"
                      rel="sponsored noreferrer"
                      aria-label="view on amazon"
                      className={clsx(
                        'flex h-10 w-full cursor-pointer items-center justify-center rounded-3xl text-center uppercase',
                        longWordsLanguages.includes(language) ? 'px-1 text-base desktop:text-sm' : 'text-base',
                        stylesBydomain.LISTPAGE_BUTTON_PRIMARY,
                        'listpage_button_primary',
                      )}
                    >
                      {translations.ProductCard.ctaText()}
                    </a>
                  </div>
                  {!!discount && (
                    <SaleEventBadge
                      redirectUrl={redirectUrl}
                      handleCardClick={handleCardClick}
                      brand={brand}
                      index={index}
                    />
                  )}
                </div>
                {/* ------------------------------------------------ Variants Mobile------------------------------------------------ */}
                <div className="flex w-full flex-row items-center gap-1 desktop:hidden tablet:mt-2">
                  {showVariantsIndication && (
                    <a
                      href={redirectUrl}
                      onClick={handleCardClick}
                      aria-label={`view ${brand}`}
                      target="_blank"
                      rel="sponsored noreferrer"
                    >
                      <VariantsIndication
                        className="flex flex-row gap-1 truncate"
                        index={index + 1}
                        variations={product?.variationDetails}
                        reportVariantsClicked={reportVariantsClicked}
                      />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
